import * as React from "react";
import {
  useDispatch,
  useMappedState
} from "redux-react-hook";
import { ColumnDef } from "@tanstack/react-table";
import { useHistory } from "react-router-dom";

import Grid from "src/library/components/Hedron";
import { LikeToDo } from "src/library/components/LikeToDo";
import Table from "src/core/dashboard/components/widgets/table/Table";
import { ListActionsList } from "src/core/lists/components/ListActionsList";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { CreateListContext } from "src/core/shared/components/HeaderContext/CreateListContext";
import { listsTableColumns } from "src/core/shared/components/tables/listsTableColumns";
import {
  listsApi,
  IList,
  DropListParams,
  GetDropListsParams
} from "src/core/lists/api/lists.api";
import {
  FilterParams,
  PaginatedData,
  PaginatedParams
} from "src/core/shared/api";
import { setLists } from "src/core/lists/store/lists.actions";
import type { IStore } from "src/core/shared/store";
import useList from "src/core/lists/hooks/useList";
import { PageProps } from "src/pages/Router/routes";
import { useDrop } from "src/core/drops/hooks";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import { usePriceVisibility } from "src/core/prices/hooks/use-price-visibility";
import { defaultPagination } from "src/core/lists/store/lists.reducer";
import useTerminology from "src/core/shared/hooks/useTerminology";
import { useCurrentUser } from "src/core/users/hooks";

const ListsPage: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const dispatch = useDispatch();
  const { erp } = useMappedState(({ global }: IStore) => ({ erp: global.settings && global.settings.erp.name }));
  const [ { currentRole: role } ] = useCurrentUser();
  const terminology = useTerminology();
  const [ { drop } ] = useDrop();
  const [ listsResponse, getLists ] = listsApi<PaginatedData<IList>, DropListParams>("GET_DROP_LISTS");
  const history = useHistory();
  const listsData = listsResponse.data;
  const [ { isCreatingList }, { createList } ] = useList();
  const checkUserPermission = useCheckUserPermission();
  const pricesVisible = usePriceVisibility();

  const onFetchData = React.useCallback((params: {filters: FilterParams<IList>, pagination: PaginatedParams<IList>}) => {
    if (drop?.id) {
      getLists<GetDropListsParams>({
        dropId: drop.id,
        params
      });
    }
  }, [ drop?.id ]);

  React.useEffect(() => {
    if (listsData?.result) {
      dispatch(setLists(listsData.result));
    }
  }, [ dispatch, listsData?.result ]);

  const isLoading = listsResponse.pending;

  const tableColumns: Array<ColumnDef<IList, keyof IList>> = React.useMemo(() => {
    return listsTableColumns({
      actionButton: info => {
        if (role && erp) {
          return (
            <ListActionsList
              list={info.row.original}
              role={role}
              terminology={props.terminology}
              erp={erp}
            />
          );
        }

        return null;
      },
      pricesVisible,
      terminology: props.terminology
    }) ;
  }, [
    erp,
    pricesVisible,
    props.terminology,
    role
  ]);

  const tableData = React.useMemo(() => {
    return listsData?.result && Array.isArray(listsData?.result) ? listsData.result : [];
  }, [ listsData ]);

  return (
    <Layout {...props}>
      <MainContentWrapper
        fill
        Header={checkUserPermission("create-list") && (
          <>
            <LikeToDo
              copy="What would you like to do?"
              buttons={[
                <CreateListContext
                  key="create-list-button"
                  createList={createList}
                  loading={isCreatingList}
                />
              ]}
            />
          </>
        )}
      >
        {drop && (
          <Grid.Bounds direction="vertical">
            <Grid.Box>
              {tableColumns.length && (
                <Table
                  tableOf={terminology?.list.plural.text || "Lists"}
                  className="large"
                  data={tableData}
                  onFetchData={onFetchData}
                  defaultPagination={defaultPagination}
                  defaultVisibility={{
                    createdAt: false,
                    updatedAt: false
                  }}
                  loading={isLoading}
                  pageCount={listsData ? listsData.pages : 0}
                  rowCount={listsData ? listsData.count : 0}
                  error={listsResponse.error}
                  columns={tableColumns}
                  mainAction={row => history.push(`/lists/${row.original.id}`)}
                />
              )}
            </Grid.Box>
          </Grid.Bounds>
        )}
      </MainContentWrapper>
    </Layout>
  );
};

export default ListsPage;
